import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Menu, Item, Text } from '@adobe/react-spectrum';
import { Selection } from '@react-types/shared';
import type { RootState } from 'types';
import { useRsdFormsSlice } from 'app/components/RsdForms/store/slice';
import {
  getRoadmapMomentTypeMenuOptions,
  type RoadmapMomentTypeMenuOptionsParams
} from './momentTypeRoadmapFilter';
import MenuCheckbox from './MenuCheckbox';
import styles from './FilterSection.module.scss';

export interface MomentTypeMenuOption {
  key: string;
  label: string;
}

interface MomentTypeFilterProps {
  /** Controlled selection (moment type ids + optional synthetic `all`). */
  selectedKeys: Selection;
  onSelectionChange: (selection: Selection) => void;
  onAction?: (key: React.Key) => void;
  /** Roadmap includes EOL; milestones grid report does not. Default true. */
  includeEolMomentType?: boolean;
}

/**
 * Reads `rsdForms.momentTypes` from Redux and renders the multi-select menu.
 * Parent supplies selection handlers. If there are no types in state, shows "No options".
 */
export function MomentTypeFilter({
  selectedKeys,
  onSelectionChange,
  onAction,
  includeEolMomentType = true
}: MomentTypeFilterProps) {
  useRsdFormsSlice();
  const momentTypes = useSelector(
    (s: RootState) => s.rsdForms?.momentTypes ?? []
  );

  const menuParams: RoadmapMomentTypeMenuOptionsParams = useMemo(
    () => ({ includeEol: includeEolMomentType }),
    [includeEolMomentType]
  );

  const options: MomentTypeMenuOption[] = useMemo(
    () => getRoadmapMomentTypeMenuOptions(momentTypes, menuParams),
    [momentTypes, menuParams]
  );

  return (
    <Menu
      data-testid="filter-section-moment-type-menu"
      selectedKeys={selectedKeys}
      onSelectionChange={onSelectionChange}
      onAction={onAction}
      selectionMode="multiple"
      UNSAFE_className={styles['subMenu']}
    >
      {options.length > 0 ? (
        options.map(option => (
          <Item
            key={option.key}
            data-testid={`filter-section-moment-type-option-${option.key}`}
          >
            <Text UNSAFE_className={styles.menuItemText}>
              <MenuCheckbox
                isSelected={
                  selectedKeys instanceof Set
                    ? selectedKeys.has(option.key)
                    : false
                }
              />
              <span className={styles.menuItemLabel} title={option.label}>
                {option.label}
              </span>
            </Text>
          </Item>
        ))
      ) : (
        <Item key="moment-type-no-options">
          <Text>No options</Text>
        </Item>
      )}
    </Menu>
  );
}

MomentTypeFilter.displayName = 'MomentTypeFilter';
